import { call, put, takeLatest } from "redux-saga/effects";
import Swal from "sweetalert2";
import { API_BASE_URL, extractErrorMessage, getRequest, postRequest } from "../../../api";
import {
    CONFIRM_PAYSLIPS_IMPORT_REQUEST,
    FETCH_MY_PAYSLIPS_REQUEST,
    FETCH_PAYSLIP_COVERAGE_REQUEST,
    PREVIEW_PAYSLIPS_REQUEST,
    confirmPayslipsImportFailure,
    confirmPayslipsImportSuccess,
    fetchPayslipCoverageFailure,
    fetchPayslipCoverageSuccess,
    fetchMyPayslipsFailure,
    fetchMyPayslipsSuccess,
    previewPayslipsFailure,
    previewPayslipsSuccess,
    setPayslipLoading,
} from "../actions/payslipActions";

const toImportedPeriod = (item) => {
    const periodDate = item?.periodDate ? String(item.periodDate) : "";
    return periodDate.length >= 7 ? periodDate.slice(0, 7) : null;
};

function* fetchCoverageChecks(token, importedPayslips) {
    const periods = Array.from(new Set((importedPayslips || []).map(toImportedPeriod).filter(Boolean)));
    const checks = [];

    for (const period of periods) {
        try {
            const response = yield call(getRequest, {
                url: `${API_BASE_URL}/api/payslips/coverage?period=${encodeURIComponent(period)}`,
                token,
            });
            if (response?.data) {
                checks.push(response.data);
            }
        } catch {
            // The import succeeded; coverage is only an informative follow-up.
        }
    }

    return checks;
}

export function* previewPayslipsSaga(action) {
    const token = sessionStorage.getItem("token");
    const files = action.payload || [];
    const url = `${API_BASE_URL}/api/payslips/import/preview`;
    const formData = new FormData();

    files.forEach((file) => {
        if (file) {
            formData.append("files", file);
        }
    });

    yield put(setPayslipLoading("preview"));
    try {
        const response = yield call(postRequest, { url, data: formData, token });
        const rows = response?.data || [];
        const coverageChecks = yield* fetchCoverageChecks(token, rows);
        yield put(previewPayslipsSuccess(rows, coverageChecks));
    } catch (error) {
        const message = extractErrorMessage(error, "Erreur lors de l'analyse des fiches de paie.");
        yield put(previewPayslipsFailure(message));
        yield call([Swal, Swal.fire], {
            icon: "error",
            title: "Analyse impossible",
            text: message,
            confirmButtonText: "OK",
        });
    } finally {
        yield put(setPayslipLoading(null));
    }
}

export function* confirmPayslipsImportSaga(action) {
    const token = sessionStorage.getItem("token");
    const url = `${API_BASE_URL}/api/payslips/import/confirm`;
    const tempIds = action.payload || [];

    yield put(setPayslipLoading("confirm"));
    try {
        const response = yield call(postRequest, {
            url,
            data: { tempIds },
            token,
        });
        const summary = response?.data || {};
        const coverageChecks = yield* fetchCoverageChecks(token, summary.importedPayslips || []);
        const enrichedSummary = { ...summary, coverageChecks };

        yield put(confirmPayslipsImportSuccess(enrichedSummary));

        yield call([Swal, Swal.fire], {
            icon: "success",
            title: "Import terminé",
            text: summary.message || "Import des fiches de paie terminé.",
            timer: 2200,
            showConfirmButton: false,
        });
    } catch (error) {
        const message = extractErrorMessage(error, "Erreur lors de la confirmation de l'import.");
        yield put(confirmPayslipsImportFailure(message));
        yield call([Swal, Swal.fire], {
            icon: "error",
            title: "Confirmation impossible",
            text: message,
            confirmButtonText: "OK",
        });
    } finally {
        yield put(setPayslipLoading(null));
    }
}

export function* fetchMyPayslipsSaga() {
    const token = sessionStorage.getItem("token");
    const url = `${API_BASE_URL}/api/payslips/my`;

    yield put(setPayslipLoading("list"));
    try {
        const response = yield call(getRequest, { url, token });
        yield put(fetchMyPayslipsSuccess(response?.data || []));
    } catch (error) {
        const message = extractErrorMessage(error, "Erreur lors du chargement des bulletins de paie.");
        yield put(fetchMyPayslipsFailure(message));
    } finally {
        yield put(setPayslipLoading(null));
    }
}

export function* fetchPayslipCoverageSaga(action) {
    const token = sessionStorage.getItem("token");
    const period = action.payload;
    const url = `${API_BASE_URL}/api/payslips/coverage?period=${encodeURIComponent(period)}`;

    yield put(setPayslipLoading("coverage"));
    try {
        const response = yield call(getRequest, { url, token });
        yield put(fetchPayslipCoverageSuccess(response?.data || null));
    } catch (error) {
        const message = extractErrorMessage(error, "Erreur lors du chargement du suivi des bulletins de paie.");
        yield put(fetchPayslipCoverageFailure(message));
    } finally {
        yield put(setPayslipLoading(null));
    }
}

export default function* payslipSaga() {
    yield takeLatest(PREVIEW_PAYSLIPS_REQUEST, previewPayslipsSaga);
    yield takeLatest(CONFIRM_PAYSLIPS_IMPORT_REQUEST, confirmPayslipsImportSaga);
    yield takeLatest(FETCH_MY_PAYSLIPS_REQUEST, fetchMyPayslipsSaga);
    yield takeLatest(FETCH_PAYSLIP_COVERAGE_REQUEST, fetchPayslipCoverageSaga);
}
